@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.yard

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.example.project.dashboard.ContentOffset
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginekartei.KarteiApi
import org.example.project.engineservice.ServiceApi
import org.example.project.utils.All
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.CellColor
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.engineslot.SLOT_BEREICH_WA
import org.example.project.engineslot.SLOT_BEREICH_WE
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.engineslot.SlotActionBarRow
import org.example.project.engineslot.SlotStyles
import org.example.project.engineslot.SlotToolbarAction
import org.example.project.engineslot.slotStandortOptions
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent
import org.example.project.service.ZustandManager
import org.example.project.service.HofService
import org.example.project.service.YardApi
import org.example.project.service.YardEngineApi
import org.example.project.service.YardFlowApi
import org.example.project.service.YardLiveSync
import org.example.project.service.UserApi
import kotlin.js.Date

private object LeitstandHofStyles : StyleSheet() {
    val screenScroll by style {
        width(100.percent)
        height(100.percent)
        minHeight(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        overflowY("auto")
        property("overflow-x", "hidden")
        property("box-sizing", "border-box")
        property("overscroll-behavior", "contain")
        property("scrollbar-width", "thin")
        property("scrollbar-color", "rgba(56, 62, 74, 0.98) transparent")

        self + "::-webkit-scrollbar" style {
            width(12.px)
            height(12.px)
            backgroundColor(Color.transparent)
        }

        self + "::-webkit-scrollbar-track" style {
            backgroundColor(Color.transparent)
        }

        self + "::-webkit-scrollbar-thumb" style {
            borderRadius(999.px)
            property(
                "background",
                "linear-gradient(180deg, rgba(72, 79, 92, 0.98) 0%, rgba(46, 52, 62, 0.98) 100%)"
            )
            property("border", "1px solid rgba(255,255,255,0.10)")
        }

        self + "::-webkit-scrollbar-corner" style {
            backgroundColor(Color.transparent)
        }
    }
}

private enum class HofViewIconSize(val iconPx: Int) {
    Header(20),
    Popup(18)
}

@Composable
private fun HofViewIcon(
    icon: String,
    title: String,
    size: HofViewIconSize = HofViewIconSize.Popup,
    onClick: () -> Unit
) {
    Img(src = icon, attrs = {
        style {
            width(size.iconPx.px)
            height(size.iconPx.px)
            padding(0.px)
            cursor("pointer")
            property("filter", "brightness(0) invert(1)")
        }
        title(title)
        onClick { onClick() }
    })
}

@Composable
fun LeitstandHofScreen() {
    Style(LeitstandHofStyles)
    Style(SlotStyles)

    val scope = rememberCoroutineScope()

    var standorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedStandortId by remember { mutableStateOf<String?>(null) }
    var unternehmen by remember { mutableStateOf<All?>(null) }

    var waLkw by remember { mutableStateOf<List<All>>(emptyList()) }
    var waKopf by remember { mutableStateOf<List<All>>(emptyList()) }
    var waStandorteByTour by remember { mutableStateOf<Map<String, List<All>>>(emptyMap()) }
    var weKopf by remember { mutableStateOf<List<All>>(emptyList()) }
    var weLkw by remember { mutableStateOf<List<All>>(emptyList()) }
    var palettenDefs by remember { mutableStateOf<List<All>>(emptyList()) }
    var kontrolleDefs by remember { mutableStateOf<List<All>>(emptyList()) }
    var aktivitaetsFeed by remember { mutableStateOf<List<All>>(emptyList()) }
    var dokumentStatus by remember { mutableStateOf<Map<String, Set<String>>>(emptyMap()) }
    var mobileMitarbeiter by remember { mutableStateOf<List<All>>(emptyList()) }
    var selectedLkwLieferscheinKey by remember { mutableStateOf<String?>(null) }
    var selectedAuskunftKey by remember { mutableStateOf<String?>(null) }
    var selectedDetailLieferscheinKey by remember { mutableStateOf<String?>(null) }
    var lieferscheinUploadEntry by remember { mutableStateOf<All?>(null) }
    var searchedLkw by remember { mutableStateOf<List<All>?>(null) }
    var searchedKopf by remember { mutableStateOf<List<All>?>(null) }
    var searchedDetails by remember { mutableStateOf<List<All>?>(null) }
    var searchedAktivitaetsFeed by remember { mutableStateOf<List<All>?>(null) }
    var viewHeightPx by remember { mutableStateOf(420) }
    var isViewResizeDragging by remember { mutableStateOf(false) }
    var isHofViewDetached by remember { mutableStateOf(false) }
    var isHofViewHidden by remember { mutableStateOf(false) }
    var isHofViewFullscreen by remember { mutableStateOf(false) }
    val selectedStandort = standorte.firstOrNull { it.id == selectedStandortId }

    if (isViewResizeDragging) {
        DisposableEffect(Unit) {
            val moveHandler: (Event) -> Unit = { event ->
                val mouse = event as? MouseEvent
                if (mouse != null) {
                    viewHeightPx = (mouse.clientY - 86).coerceIn(220, 760)
                }
            }
            val upHandler: (Event) -> Unit = { isViewResizeDragging = false }
            window.addEventListener("mousemove", moveHandler)
            window.addEventListener("mouseup", upHandler)
            onDispose {
                window.removeEventListener("mousemove", moveHandler)
                window.removeEventListener("mouseup", upHandler)
            }
        }
    }

    suspend fun reloadHofData() {
        val standortId = selectedStandortId.orEmpty()
        val standortLabel = standorte.firstOrNull { it.id == standortId }?.standort.orEmpty()

        waKopf = emptyList()
        waLkw = emptyList()
        waStandorteByTour = emptyMap()
        weKopf = emptyList()
        weLkw = emptyList()
        aktivitaetsFeed = emptyList()
        dokumentStatus = emptyMap()
        selectedLkwLieferscheinKey = null
        selectedAuskunftKey = null
        selectedDetailLieferscheinKey = null
        lieferscheinUploadEntry = null
        searchedLkw = null
        searchedKopf = null
        searchedDetails = null
        searchedAktivitaetsFeed = null
        HofService.bind(null, emptyList())

        val allWaKopf = YardApi.loadWaListe()
        val allWaLkw = YardApi.loadWaLkwListe()
        val allWeKopf = YardApi.loadWeListe()
        val allWeLkw = YardApi.loadWeLkwListe()
        val allWaStandorteByTour = allWaKopf
            .map { it.lieferscheinnummer }
            .filter { it.isNotBlank() }
            .distinct()
            .associateWith { YardApi.loadWaStandorteByTour(it) }

        waStandorteByTour = allWaStandorteByTour
            .mapValues { (_, rows) ->
                rows.filter { leitstandMatchesStandort(it, standortId, standortLabel) }
            }
            .filterValues { it.isNotEmpty() }
        waLkw = allWaLkw.filter { leitstandMatchesStandort(it, standortId, standortLabel) }
        waKopf = allWaKopf.filter { kopf ->
            val tour = kopf.lieferscheinnummer
            tour.isNotBlank() && (
                waStandorteByTour[tour].orEmpty().isNotEmpty() ||
                    waLkw.any { it.lieferscheinnummer == tour }
                )
        }
        weLkw = allWeLkw.filter { leitstandMatchesStandort(it, standortId, standortLabel) }
        weKopf = allWeKopf.filter { kopf ->
            weLkw.any { lkw -> leitstandWeSameAuftrag(kopf, lkw) }
        }
        palettenDefs = YardApi.loadPalettenDefinitionen().filter { it.aktivWa }
        kontrolleDefs = YardApi.loadKontrolleDefinitionen()
        aktivitaetsFeed = loadLeitstandAktivitaetsFeed(weLkw, waLkw, standortId, standortLabel)
        dokumentStatus = loadLeitstandDokumentStatus(weLkw, waLkw)

        val layout = selectedStandortId?.let { YardApi.loadStandortLayout(it) }
        HofService.bind(layout, waLkw + weLkw)
    }

    LaunchedEffect(Unit) {
        standorte = KarteiApi.loadStandorte()
        unternehmen = runCatching { KarteiApi.loadUnternehmen() }.getOrNull()
        mobileMitarbeiter = UserApi.loadUsers()
            .filter { it.aktiv && it.gesperrt != true }
            .sortedWith(compareBy<All> { it.nachname.lowercase() }.thenBy { it.vorname.lowercase() })
        if (selectedStandortId == null) {
            selectedStandortId = standorte.firstOrNull()?.id
        }

        reloadHofData()
    }

    val alleLkw = remember(waLkw, weLkw) { waLkw + weLkw }
    val assignedBlinkTorIds = remember(alleLkw) {
        alleLkw
            .filter { it.zustand == ZustandManager.TOR_ZUGEWIESEN }
            .mapNotNull { it.tor }
            .toSet()
    }
    val occupiedTorIds = remember(alleLkw) {
        alleLkw
            .filter { it.zustand >= ZustandManager.TOR_ZUGEWIESEN && it.zustand < ZustandManager.ABGESCHLOSSEN }
            .mapNotNull { it.tor }
            .toSet()
    }

    val lkwSortiert = remember(alleLkw) {
        alleLkw.sortedWith(
            compareBy<All> {
                if (it.ablaufPrio > 0) it.ablaufPrio else Int.MAX_VALUE
            }.thenBy {
                it.prio.toIntOrNull() ?: Int.MAX_VALUE
            }.thenBy {
                it.uhrzeitAnmeldung
            }
        )
    }
    val standortOptions = remember(standorte) { slotStandortOptions(standorte) }
    val firmenname = unternehmen?.firma
        ?.ifBlank { selectedStandort?.firma.orEmpty() }
        ?.ifBlank { selectedStandort?.standort.orEmpty() }
        .orEmpty()
    val yardLiveVersion = YardLiveSync.version()
    val standortScreenKey = selectedStandortId.orEmpty().ifBlank { "kein-standort" }
    val visibleAktivitaetsFeed = searchedAktivitaetsFeed ?: aktivitaetsFeed
    val searchAktivitaetsFeed: (Map<String, String>) -> Unit = { filters ->
        scope.launch {
            searchedAktivitaetsFeed = YardApi.searchYardAktivitaetsfeed(
                filters,
                selectedStandortId.orEmpty(),
                selectedStandort?.standort.orEmpty()
            )
        }
    }

    LaunchedEffect(yardLiveVersion, selectedStandortId) {
        if (selectedStandortId != null) {
            reloadHofData()
        }
    }

    Div({ classes(LeitstandHofStyles.screenScroll) }) {

        if (!isHofViewFullscreen) {
            SlotActionBarRow(
                leftContent = {},
                centerContent = {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            gap(12.px)
                            minWidth(0.px)
                            flexWrap(FlexWrap.Wrap)
                        }
                    }) {
                        if (firmenname.isNotBlank()) {
                            Span({
                                style {
                                    color(Color.white)
                                    fontSize(15.px)
                                    fontWeight("600")
                                }
                            }) { Text(firmenname) }
                        }
                        Div({
                            style {
                                minWidth(240.px)
                                property("width", "auto")
                                maxWidth(420.px)
                            }
                        }) {
                            AppDropdown(
                                options = standortOptions,
                                selectedValue = selectedStandortId,
                                onChange = { selectedStandortId = it },
                                placeholder = "Adresse",
                                appearance = AppDropdownAppearance.Underline,
                                menuMode = AppDropdownMenuMode.Overlay
                            )
                        }
                    }
                },
                rightContent = {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            gap(12.px)
                            flexWrap(FlexWrap.Wrap)
                        }
                    }) {
                        if (isHofViewHidden) {
                            SlotToolbarAction(
                                icon = "images/view.svg",
                                label = "Einblenden",
                                onClick = {
                                    isHofViewHidden = false
                                    isHofViewDetached = false
                                    isHofViewFullscreen = false
                                }
                            )
                        } else if (!isHofViewDetached) {
                            SlotToolbarAction(
                                icon = "images/ausbetten.svg",
                                label = "Ausbetten",
                                onClick = {
                                    isHofViewFullscreen = false
                                    isHofViewDetached = true
                                }
                            )
                            SlotToolbarAction(
                                icon = "images/hide.svg",
                                label = "Ausblenden",
                                onClick = {
                                    isHofViewHidden = true
                                    isHofViewDetached = false
                                    isHofViewFullscreen = false
                                }
                            )
                            SlotToolbarAction(
                                icon = "images/fullscreen.svg",
                                label = "Vollbild",
                                onClick = {
                                    isHofViewFullscreen = true
                                    isHofViewDetached = false
                                }
                            )
                        }
                    }
                }
            )
        Div({
            style {
                display(DisplayStyle.None)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                padding(16.px)
            }
        }) {
            H2({
                style {
                    display(DisplayStyle.None)
                    margin(0.px)
                }
            }) { Text("Leitstand - Hofübersicht") }

            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                }
            }) {
                if (isHofViewHidden) {
                    Button(attrs = {
                        style {
                            padding(8.px, 12.px)
                            fontSize(14.px)
                            color(Color.white)
                            backgroundColor(Color("rgba(255,255,255,0.08)"))
                            border(1.px, LineStyle.Solid, Color("rgba(255,255,255,0.18)"))
                            borderRadius(8.px)
                            cursor("pointer")
                        }
                        onClick {
                            isHofViewHidden = false
                            isHofViewDetached = false
                            isHofViewFullscreen = false
                        }
                    }) { Text("Einblenden") }
                } else if (!isHofViewDetached) {
                    Button(attrs = {
                        style {
                            padding(8.px, 12.px)
                            fontSize(14.px)
                            color(Color.white)
                            backgroundColor(Color("rgba(255,255,255,0.08)"))
                            border(1.px, LineStyle.Solid, Color("rgba(255,255,255,0.18)"))
                            borderRadius(8.px)
                            cursor("pointer")
                        }
                        onClick {
                            isHofViewFullscreen = false
                            isHofViewDetached = true
                        }
                    }) { Text("Ausbetten") }

                    HofViewIcon(
                        icon = "images/hide.svg",
                        title = "Ausblenden",
                        size = HofViewIconSize.Header
                    ) {
                        isHofViewHidden = true
                        isHofViewDetached = false
                        isHofViewFullscreen = false
                    }
                }

                AppDropdown(
                    options = standorte.map { s ->
                        All(key = s.id, label = s.standort.ifBlank { "Standort ${s.id}" })
                    },
                    selectedValue = selectedStandortId,
                    onChange = { selectedStandortId = it },
                    attrs = {
                        style {
                            padding(8.px)
                            fontSize(16.px)
                            width(300.px)
                        }
                    }
                )
            }
        }
        }

        if (!isHofViewDetached && !isHofViewHidden && !isHofViewFullscreen) {
        Div({
            style {
                height(viewHeightPx.px)
                minHeight(220.px)
                margin(0.px, 16.px, 0.px, 16.px)
                display(DisplayStyle.Flex)
                gap(14.px)
                minWidth(0.px)
                flexShrink(0)
            }
        }) {
            Div({
                style {
                    flex(1)
                    minWidth(0.px)
                    backgroundColor(Color.transparent)
                    borderRadius(8.px)
                    overflow("hidden")
                }
            }) {
                HofMapView(
                    standortId = selectedStandortId,
                    highlightTor = null,
                    occupiedTorIds = occupiedTorIds,
                    assignedBlinkTorIds = assignedBlinkTorIds,
                    blinkAssignedTors = true,
                    fitToBounds = true,
                    fitHeightPx = viewHeightPx
                )
            }

            Div({
                style {
                    width(760.px)
                    minWidth(720.px)
                    flexShrink(0)
                    height(100.percent)
                    overflow("hidden")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.None)
                        fontWeight("bold")
                        color(Color("#dce3ef"))
                        fontSize(15.px)
                    }
            }) { Text("Aktivitätsverlauf") }

                Div({
                    style {
                        flex(1)
                        minHeight(0.px)
                        overflow("hidden")
                    }
                }) {
                    YardAktivitaetsfeedTable(
                        screenKey = "Leitstand_Aktivitaetsverlauf_$standortScreenKey",
                        rollenKey = "LeitstandHofScreen",
                        aktivitaetsFeed = visibleAktivitaetsFeed,
                        onSearchWithFilters = searchAktivitaetsFeed
                    )
                }

            }
        }
        }

        if (!isHofViewFullscreen) {
        Div({
            style {
                height(12.px)
                margin(0.px, 16.px, 14.px, 16.px)
                cursor("row-resize")
                flexShrink(0)
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
            }
            onMouseDown {
                isViewResizeDragging = true
                it.preventDefault()
            }
        }) {
            Div({
                style {
                    height(4.px)
                    width(64.px)
                    borderRadius(999.px)
                    backgroundColor(Color("#ffffff"))
                    opacity(0.75)
                }
            })
        }

        }

        if (isHofViewDetached && !isHofViewHidden && !isHofViewFullscreen) {
            PopupEngineCard(
                windowKey = "leitstand-hofview-popup",
                widthPx = 1320,
                heightPx = 720,
                minWidthPx = 900,
                minHeightPx = 520,
                initialX = 220.0,
                initialY = 120.0,
                contentInsetLeft = ContentOffset.LEFT.value.toDouble(),
                contentInsetTop = ContentOffset.TOP.value.toDouble(),
                contentInsetRight = ContentOffset.RIGHT.value.toDouble(),
                contentInsetBottom = ContentOffset.BOTTOM.value.toDouble(),
                showCloseIcon = false,
                onClose = { isHofViewDetached = false }
            ) {
                Div({
                    style {
                        height(100.percent)
                        minHeight(0.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        padding(18.px)
                        gap(12.px)
                        boxSizing("border-box")
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            position(Position.Relative)
                            flexShrink(0)
                            minHeight(24.px)
                        }
                    }) {
                        Div({
                            style {
                                position(Position.Absolute)
                                left(0.px)
                                top(50.percent)
                                property("transform", "translateY(-50%)")
                            }
                        }) {
                            HofViewIcon(
                                icon = "images/einbetten.svg",
                                title = "Einbetten"
                            ) {
                                isHofViewDetached = false
                            }
                        }

                        H3({
                            style {
                                margin(0.px)
                                color(Color.white)
                                fontSize(18.px)
                                textAlign("center")
                            }
                        }) { Text("Hofview") }
                    }

                    LeitstandHofViewArea(
                        selectedStandortId = selectedStandortId,
                        occupiedTorIds = occupiedTorIds,
                        assignedBlinkTorIds = assignedBlinkTorIds,
                        aktivitaetsFeed = visibleAktivitaetsFeed,
                        onSearchWithFilters = searchAktivitaetsFeed,
                        viewHeightPx = 610,
                        marginCss = "0",
                        screenKeySuffix = "popup_$standortScreenKey",
                        fillHeight = true
                    )
                }
            }
        }

        if (isHofViewFullscreen && !isHofViewHidden) {
            Div({
                style {
                    position(Position.Fixed)
                    left(ContentOffset.LEFT)
                    right(ContentOffset.RIGHT)
                    top(ContentOffset.TOP)
                    bottom(ContentOffset.BOTTOM)
                    property("z-index", "160000")
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    padding(0.px)
                    gap(0.px)
                    boxSizing("border-box")
                }
            }) {
                LeitstandHofViewArea(
                    selectedStandortId = selectedStandortId,
                    occupiedTorIds = occupiedTorIds,
                    assignedBlinkTorIds = assignedBlinkTorIds,
                    aktivitaetsFeed = visibleAktivitaetsFeed,
                    onSearchWithFilters = searchAktivitaetsFeed,
                    viewHeightPx = (window.innerHeight - ContentOffset.TOP.value - ContentOffset.BOTTOM.value).toInt().coerceAtLeast(520),
                    marginCss = "0",
                    screenKeySuffix = "fullscreen_$standortScreenKey",
                    fillHeight = true,
                    bottomRightIcon = "images/closefullscreen.svg",
                    bottomRightTitle = "Vollbild beenden",
                    onBottomRightIconClick = {
                        isHofViewFullscreen = false
                        isHofViewDetached = false
                    }
                )
            }
        }

        if (!isHofViewFullscreen) {
        Div({
            style {
                flexShrink(0)
                overflow("visible")
                padding(0.px, 16.px, 16.px, 16.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(16.px)
            }
        }) {
            val fiveColumnWidth = "calc((100% - 56px) / 5)"
            val headers = listOf(
                "LKW/Slot ID", "Typ", "Zeitfenster", "Angemeldet", "Zustand", "LS", "PIN", "Tor", "Fahrer", "LKW", "Auflieger", "WA Platz",
                "Prio", "Bearb.-Prio", "Mitarbeiter", "Paletten Soll", "Paletten Ist", "Offen",
                "Paletten Eingang", "Paletten Ausgang", "Vorgeladen", "WE Fehlerprotokoll", "Bruch", "Fehlmenge", "Überzählig", "PIN bestätigt",
                "Lieferschein", "Palettenschein", "Qualitätskontrolle", "Prozessdokumentation"
            )
            val angekommenByAuftrag = remember(aktivitaetsFeed) {
                leitstandAngekommenZeiten(aktivitaetsFeed)
            }

            val editableCols = setOf("Bearb.-Prio", "Tor")
            var selectedLkwRows by remember(standortScreenKey) { mutableStateOf(emptySet<String>()) }
            var ausstehendeMaZuweisungen by remember(standortScreenKey) {
                mutableStateOf<Map<String, String>>(emptyMap())
            }
            val selectedMaLkw = lkwSortiert.singleOrNull {
                leitstandLkwRowKey(it) in selectedLkwRows
            }
            val maZuweisungAenderbar = selectedLkwRows.size == 1 &&
                selectedMaLkw != null && selectedMaLkw.zustand <= ZustandManager.RESERVIERT
            val maZuweisungOptionen = remember(mobileMitarbeiter) {
                listOf("" to "Zuweisung entfernen") + mobileMitarbeiter.map { user ->
                    user.id to leitstandMitarbeiterAuswahlLabel(user)
                }
            }

            val zustandIdx = headers.indexOf("Zustand")
            val cellColors = lkwSortiert.associate {
                (leitstandLkwRowKey(it) to zustandIdx) to
                        leitstandZustandCellColor(it)
            }
            val torBySammelKey = remember(lkwSortiert) {
                lkwSortiert
                    .groupBy { leitstandSammelKey(it) }
                    .filterKeys { it.isNotBlank() }
                    .mapValues { (_, group) -> group.firstNotNullOfOrNull { it.tor } }
                    .filterValues { it != null }
                    .mapValues { it.value.toString() }
            }
            val selectedLkwLieferscheinUploadEntry = lkwSortiert.firstOrNull {
                leitstandLkwRowKey(it) == selectedLkwLieferscheinKey &&
                    leitstandAuftragsTyp(it) == SLOT_BEREICH_WA
            }

            Div({
                style {
                    height(1260.px)
                    minHeight(920.px)
                    width(100.percent)
                }
            }) {
                TabularSplitScreen(
                    initialRatio = 0.38f,
                    screen1 = {
            TabularScreen(
                screenKey = "Leitstand_LKW_$standortScreenKey",
                rollenKey = "LeitstandHofScreen",
                title = "WE / WA LKW Übersicht",
                headers = headers,
                data = (searchedLkw ?: lkwSortiert).map { lkw ->
                    val pin = yardPinCode(lkw)
                    val typ = leitstandAuftragsTyp(lkw)
                    val waStandort = leitstandWaStandortForLkw(lkw, waStandorteByTour)
                    val soll = lkw.palettenanzahl
                    val ist = if (typ == "WA") leitstandWaIstPalettenanzahl(lkw) else leitstandWeIstPalettenanzahl(lkw)
                    val bruch = if (typ == "WE") leitstandWeExtraInt(lkw, lkw.mobilWePalettenBruch) else 0
                    val fehlmenge = if (typ == "WE") leitstandWeExtraInt(lkw, lkw.mobilWePalettenFehlmenge) else 0
                    val ueberzaehlig = if (typ == "WE") leitstandWeExtraInt(lkw, lkw.mobilWePalettenUeberzaehlig) else 0
                    val eingang = if (typ == "WA") lkw.leerpalettenanzahl else ist
                    val ausgang = if (typ == "WA") ist else if (leitstandWeAbschlussBestaetigt(lkw)) lkw.leerpalettenanzahl else 0
                    val offen = (soll - ist).coerceAtLeast(0)
                    val dokumente = dokumentStatus[leitstandLkwRowKey(lkw)].orEmpty()
                    val ausstehendeMaUserId = ausstehendeMaZuweisungen[leitstandLkwRowKey(lkw)]
                    val angezeigtesMaKuerzel = if (ausstehendeMaUserId != null) {
                        mobileMitarbeiter
                            .firstOrNull { it.id == ausstehendeMaUserId }
                            ?.let(::leitstandMitarbeiterKuerzel)
                            .orEmpty()
                    } else {
                        leitstandBearbeiterKuerzel(lkw)
                    }
                    leitstandLkwRowKey(lkw) to listOf(
                        lkw.id.ifBlank { pin },
                        typ,
                        leitstandTerminZeitfenster(lkw),
                        leitstandAngekommenAnzeige(lkw, angekommenByAuftrag),
                        "${lkw.zustand} - ${leitstandZustandText(typ, lkw.zustand)}",
                        leitstandLieferscheinnummer(lkw),
                        pin,
                        leitstandTorAnzeige(lkw, waStandort, torBySammelKey),
                        lkw.fahrername,
                        lkw.lkwkennzeichen,
                        lkw.aufliegerkennzeichen,
                        leitstandPlatzAnzeige(lkw, waStandort),
                        lkw.prio,
                        lkw.ablaufPrio.toString(),
                        angezeigtesMaKuerzel,
                        soll.takeIf { it > 0 }?.toString().orEmpty(),
                        ist.takeIf { it > 0 }?.toString().orEmpty(),
                        offen.toString(),
                        eingang.takeIf { it > 0 }?.toString().orEmpty(),
                        if (typ == "WE" && leitstandWeAbschlussBestaetigt(lkw)) ausgang.toString() else ausgang.takeIf { it > 0 }?.toString().orEmpty(),
                        leitstandVorgeladenPalettenAnzeige(lkw, typ),
                        leitstandDokumentCheckbox(dokumente, "we fehlerprotokoll"),
                        bruch.takeIf { it > 0 }?.toString().orEmpty(),
                        fehlmenge.takeIf { it > 0 }?.toString().orEmpty(),
                        ueberzaehlig.takeIf { it > 0 }?.toString().orEmpty(),
                        yardPinBestaetigtLabel(lkw),
                        leitstandDokumentCheckbox(dokumente, "lieferschein"),
                        leitstandDokumentCheckbox(dokumente, "palettenschein"),
                        leitstandDokumentCheckbox(dokumente, "qualitätskontrolle"),
                        leitstandDokumentCheckbox(dokumente, "prozessdokumentation")
                    )
                }, cellBackgroundColors = cellColors,
                toggleColumns = setOf("WE Fehlerprotokoll", "PIN bestätigt", "Lieferschein", "Palettenschein", "Qualitätskontrolle", "Prozessdokumentation"),
                editable = true,
                cellEditable = { rowId, column, _ ->
                    val row = lkwSortiert.firstOrNull { leitstandLkwRowKey(it) == rowId }
                    when (column) {
                        "Tor" -> row != null && leitstandAuftragsTyp(row) == SLOT_BEREICH_WE && row.zustand == ZustandManager.RESERVIERT
                        else -> true
                    }
                },
                nonEditableCols = headers.toSet() - editableCols,
                dataColumnWidthCss = fiveColumnWidth,
                extraNavButtons = listOf(
                    TabularToolbarButton(
                        label = "MA zuweisen",
                        icon = "images/user.svg",
                        enabled = maZuweisungAenderbar,
                        dropdownOptions = maZuweisungOptionen,
                        selectedDropdownValue = selectedMaLkw?.let { selected ->
                            ausstehendeMaZuweisungen[leitstandLkwRowKey(selected)]
                                ?: selected.mobileBearbeiterUserId
                        },
                        dropdownPlaceholder = "MA zuweisen",
                        onDropdownChange = { userId ->
                            val selected = selectedMaLkw ?: return@TabularToolbarButton
                            val sammelKey = leitstandSammelKey(selected)
                            val gruppenLkw = if (sammelKey.isBlank()) {
                                listOf(selected)
                            } else {
                                lkwSortiert
                                    .filter {
                                        leitstandSammelKey(it) == sammelKey &&
                                            it.zustand <= ZustandManager.RESERVIERT
                                    }
                            }
                            val neueMaUserId = userId.orEmpty()
                            val neuerMa = mobileMitarbeiter.firstOrNull { it.id == neueMaUserId }
                            ausstehendeMaZuweisungen = ausstehendeMaZuweisungen +
                                gruppenLkw.associate { leitstandLkwRowKey(it) to neueMaUserId }
                            scope.launch {
                                var erfolgreichGespeichert = true
                                gruppenLkw.forEach { lkw ->
                                    val updated = lkw.copy(
                                        mobileBearbeiterUserId = neueMaUserId,
                                        mobileBearbeiterName = neuerMa?.let(::leitstandMitarbeiterName).orEmpty(),
                                        mobileBearbeiterKuerzel = neuerMa?.let(::leitstandMitarbeiterKuerzel).orEmpty()
                                    )
                                    val gespeichert = if (leitstandAuftragsTyp(lkw) == SLOT_BEREICH_WE) {
                                        YardApi.saveWeLkwVorgang(updated) != null
                                    } else {
                                        YardApi.saveWaLkwVorgang(updated) != null
                                    }
                                    if (!gespeichert) erfolgreichGespeichert = false
                                }
                                if (!erfolgreichGespeichert) {
                                    AppDialogs.alert("MA-Zuweisung konnte nicht vollständig gespeichert werden.")
                                }
                                ausstehendeMaZuweisungen = emptyMap()
                                reloadHofData()
                            }
                        }
                    ),
                    TabularToolbarButton(
                        label = "Lieferscheine hinzufügen",
                        icon = "images/upload.svg",
                        enabled = selectedLkwLieferscheinUploadEntry != null,
                        onClick = { lieferscheinUploadEntry = selectedLkwLieferscheinUploadEntry }
                    )
                ),
                onRowClick = { rowId -> selectedLkwLieferscheinKey = rowId },
                onSelectedRowsChange = { selectedLkwRows = it },
                onSearchWithFilters = { filters ->
                    scope.launch {
                        searchedLkw = YardApi.searchLeitstandHof(
                            filters, "lkw", selectedStandortId.orEmpty(), selectedStandort?.standort.orEmpty()
                        )
                    }
                },
                onCommit = { rows ->
                    scope.launch {
                        var changedAny = false
                        rows.forEach { (rowKey, cols) ->
                            val lkw = lkwSortiert.firstOrNull { leitstandLkwRowKey(it) == rowKey }
                                ?: return@forEach
                            val typ = leitstandAuftragsTyp(lkw)
                            val torValue = cols[headers.indexOf("Tor")].trim()
                            val torNeu = if (torValue.isBlank()) null else torValue.toIntOrNull() ?: lkw.tor
                            val updated = lkw.copy(
                                ablaufPrio = cols[headers.indexOf("Bearb.-Prio")].toIntOrNull()
                                    ?: lkw.ablaufPrio,
                                tor = torNeu
                            )
                            if (updated == lkw) return@forEach
                            changedAny = true
                            if (torNeu != lkw.tor && typ == SLOT_BEREICH_WE) {
                                if (torNeu == null) {
                                    YardApi.saveWeLkwVorgang(updated.copy(torcode = ""))
                                } else {
                                    YardEngineApi.suggestWeTor(updated, updated.firmenId)
                                }
                            } else if (typ == SLOT_BEREICH_WE) {
                                YardApi.saveWeLkwVorgang(updated)
                            } else {
                                YardApi.saveWaLkwVorgang(updated)
                            }
                        }
                        if (changedAny) {
                            reloadHofData()
                        }
                    }
                }
            )
                    },
                    screen2 = {

            val auskunftKopfHeaders = leitstandAuskunftKopfHeaders(palettenDefs)
            val auskunftKopfRows = remember(weKopf, waKopf, searchedKopf, waStandorteByTour, palettenDefs) {
                val source = searchedKopf
                leitstandAuskunftKopfRows(
                    source?.filter { leitstandAuftragsTyp(it) == SLOT_BEREICH_WE } ?: weKopf,
                    source?.filter { leitstandAuftragsTyp(it) == SLOT_BEREICH_WA } ?: waKopf,
                    waStandorteByTour
                )
            }
            val auskunftZustandIdx = auskunftKopfHeaders.indexOf("Zustand")
            val auskunftCellColors = auskunftKopfRows.associate { row ->
                (row.first to auskunftZustandIdx) to CellColor(Color(ZustandManager.colorHexFor(row.second.zustand)))
            }

            Div({
                style {
                    height(760.px)
                    minHeight(620.px)
                    width(100.percent)
                }
            }) {
                TabularSplitScreen(
                    initialRatio = 0.56f,
                    screen1 = {
            TabularScreen(
                screenKey = "Leitstand_Auskunft_Kopf_$standortScreenKey",
                rollenKey = "LeitstandHofScreen",
                title = "WE / WA Auskunft",
                headers = auskunftKopfHeaders,
                models = auskunftKopfRows.map { it.second },
                data = auskunftKopfRows.map { it.first to leitstandAuskunftKopfRow(it.second, weLkw, waLkw, palettenDefs) },
                cellBackgroundColors = auskunftCellColors,
                toggleColumns = setOf("Kontrolliert"),
                editable = false,
                nonEditableCols = auskunftKopfHeaders.toSet(),
                dataColumnWidthCss = fiveColumnWidth,
                onRowClick = { rowId -> selectedAuskunftKey = rowId },
                onSearchWithFilters = { filters ->
                    scope.launch {
                        searchedKopf = YardApi.searchLeitstandHof(
                            filters, "kopf", selectedStandortId.orEmpty(), selectedStandort?.standort.orEmpty()
                        )
                    }
                },
                onCommit = {}
            )
                    },
                    screen2 = {

            val selectedDetails = remember(selectedAuskunftKey, weLkw, waLkw, waStandorteByTour) {
                leitstandAuskunftDetails(selectedAuskunftKey, weLkw, waLkw, waStandorteByTour)
            }
            if (selectedDetails.isNotEmpty()) {
                val selectedLieferscheinEntry = selectedDetails.firstOrNull {
                    leitstandAuskunftDetailKey(it) == selectedDetailLieferscheinKey
                }
                val selectedLieferscheinUploadEntry = selectedLieferscheinEntry?.takeIf { leitstandAuftragsTyp(it) == SLOT_BEREICH_WA }
                val angekommenByAuftrag = remember(aktivitaetsFeed) {
                    leitstandAngekommenZeiten(aktivitaetsFeed)
                }
                val weKontrollen = kontrolleDefs.filter { it.aktivWe }
                val waKontrollen = kontrolleDefs.filter { it.aktivWa }
                val detailHeaders = leitstandAuskunftDetailHeaders(weKontrollen, waKontrollen)
                val detailZustandIdx = detailHeaders.indexOf("Zustand")
                val detailCellColors = selectedDetails.associate { detail ->
                    (leitstandAuskunftDetailKey(detail) to detailZustandIdx) to
                            leitstandZustandCellColor(detail)
                }

                Div({
                    style {
                        width(100.percent)
                        height(100.percent)
                        minWidth(0.px)
                        overflowX("auto")
                        overflowY("hidden")
                    }
                }) {
                    Div({
                        style {
                            width(100.percent)
                            property("min-width", "${(detailHeaders.size + 1) * 170}px")
                            height(100.percent)
                        }
                    }) {
                        TabularScreen(
                            screenKey = "Leitstand_Auskunft_Details_$standortScreenKey",
                            rollenKey = "LeitstandHofScreen",
                            title = "WE / WA LKW-Details",
                            headers = detailHeaders,
                            models = selectedDetails,
                            data = (searchedDetails ?: selectedDetails).map {
                                leitstandAuskunftDetailKey(it) to
                                    leitstandAuskunftDetailRow(it, waStandorteByTour, weKontrollen, waKontrollen, angekommenByAuftrag, dokumentStatus)
                            },
                            cellBackgroundColors = detailCellColors,
                            toggleColumns = setOf("WE Fehlerprotokoll", "Lieferschein", "Palettenschein", "Qualitätskontrolle", "Prozessdokumentation"),
                            editable = false,
                            nonEditableCols = detailHeaders.toSet(),
                            dataColumnWidthCss = fiveColumnWidth,
                            extraNavButtons = listOf(
                                TabularToolbarButton(
                                    label = "Lieferscheine hinzufügen",
                                    icon = "images/upload.svg",
                                    enabled = selectedLieferscheinUploadEntry != null,
                                    onClick = { lieferscheinUploadEntry = selectedLieferscheinUploadEntry }
                                )
                            ),
                            showFillerRows = false,
                            onRowClick = { rowId -> selectedDetailLieferscheinKey = rowId },
                            onSearchWithFilters = { filters ->
                                scope.launch {
                                    val parts = selectedAuskunftKey.orEmpty().split(":")
                                    searchedDetails = YardApi.searchLeitstandHof(
                                        filters, "detail", selectedStandortId.orEmpty(), selectedStandort?.standort.orEmpty(),
                                        parts.getOrNull(0).orEmpty(), parts.getOrNull(2).orEmpty()
                                    )
                                }
                            },
                            onCommit = {}
                        )
                    }
                }
            }
                    }
                )
            }
                    }
                )
            }
            lieferscheinUploadEntry?.let { uploadEntry ->
                YardLieferscheinUploadPopup(
                    entry = uploadEntry,
                    onClose = { lieferscheinUploadEntry = null },
                    onUploaded = { uploadedEntry ->
                        dokumentStatus = loadLeitstandDokumentStatus(weLkw, waLkw)
                        val dokumente = dokumentStatus[leitstandLkwRowKey(uploadedEntry)].orEmpty()
                        val lieferscheinGefunden = "lieferschein" in dokumente
                        if (lieferscheinGefunden) {
                            lieferscheinUploadEntry = null
                        }
                        lieferscheinGefunden
                    }
                )
            }
        }
        }
    }
}

@Composable
private fun LeitstandHofViewArea(
    selectedStandortId: String?,
    occupiedTorIds: Set<Int>,
    assignedBlinkTorIds: Set<Int>,
    aktivitaetsFeed: List<All>,
    onSearchWithFilters: (Map<String, String>) -> Unit,
    viewHeightPx: Int,
    marginCss: String,
    screenKeySuffix: String,
    fillHeight: Boolean = false,
    bottomRightIcon: String? = null,
    bottomRightTitle: String = "",
    onBottomRightIconClick: (() -> Unit)? = null
) {
    Div({
        style {
            position(Position.Relative)
            if (fillHeight) {
                flex(1)
                height(100.percent)
                minHeight(0.px)
            } else {
                height(viewHeightPx.px)
                minHeight(220.px)
                flexShrink(0)
            }
            property("margin", marginCss)
            display(DisplayStyle.Flex)
            gap(14.px)
            minWidth(0.px)
        }
    }) {
        Div({
            style {
                flex(1)
                minWidth(0.px)
                backgroundColor(Color.transparent)
                borderRadius(8.px)
                overflow("hidden")
            }
        }) {
            HofMapView(
                standortId = selectedStandortId,
                highlightTor = null,
                occupiedTorIds = occupiedTorIds,
                assignedBlinkTorIds = assignedBlinkTorIds,
                blinkAssignedTors = true,
                fitToBounds = true,
                fitHeightPx = viewHeightPx
            )
        }

        Div({
            style {
                width(760.px)
                minWidth(720.px)
                flexShrink(0)
                height(100.percent)
                overflow("hidden")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
            }
        }) {
            Div({
                style {
                    flex(1)
                    minHeight(0.px)
                    overflow("hidden")
                }
            }) {
                YardAktivitaetsfeedTable(
                    screenKey = "Leitstand_Aktivitaetsverlauf_$screenKeySuffix",
                    rollenKey = "LeitstandHofScreen",
                    aktivitaetsFeed = aktivitaetsFeed,
                    onSearchWithFilters = onSearchWithFilters
                )
            }

            if (bottomRightIcon != null && onBottomRightIconClick != null) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        justifyContent(JustifyContent.FlexEnd)
                        flexShrink(0)
                        paddingTop(8.px)
                    }
                }) {
                    HofViewIcon(
                        icon = bottomRightIcon,
                        title = bottomRightTitle,
                        size = HofViewIconSize.Header,
                        onClick = onBottomRightIconClick
                    )
                }
            }
        }
    }
}

private suspend fun loadLeitstandDokumentStatus(weLkw: List<All>, waLkw: List<All>): Map<String, Set<String>> {
    val result = mutableMapOf<String, Set<String>>()
    (weLkw + waLkw)
        .filter { it.lieferscheinnummer.isNotBlank() }
        .forEach { lkw ->
            val typ = leitstandAuftragsTyp(lkw)
            val serviceType = if (typ == "WE") "yard-we" else "yard-wa"
            val documents = runCatching {
                ServiceApi.serviceDocuments(serviceType, lkw.lieferscheinnummer)
            }.getOrDefault(emptyList())
            val documentFiles = documents.map { doc ->
                doc.file
                    .ifBlank { doc.name.takeIf { it.isNotBlank() }?.let { "$it.pdf" }.orEmpty() }
                    .lowercase()
            }
            result[leitstandLkwRowKey(lkw)] = buildSet {
                if (documentFiles.any { it == yardLieferscheinFileName(lkw).lowercase() }) add("lieferschein")
                if (documentFiles.any { leitstandMatchesYardDocument(lkw, it, "palettenschein") }) add("palettenschein")
                if (documentFiles.any { leitstandMatchesYardDocument(lkw, it, "qualitaetsdokumentation") }) add("qualitätskontrolle")
                if (documentFiles.any { leitstandMatchesYardDocument(lkw, it, "prozessdokumentation") }) add("prozessdokumentation")
                if (typ == "WE" && documentFiles.any { leitstandMatchesWeFehlerprotokollDocument(lkw, it) }) add("we fehlerprotokoll")
            }
        }
    return result
}

private fun leitstandDokumentCheckbox(dokumente: Set<String>, key: String): String =
    if (key in dokumente) "\u2705" else "\u2B1C"

private fun leitstandKontrolliertCheckbox(kontrolliert: Boolean): String =
    if (kontrolliert) "\u2705" else "\u2B1C"

private fun leitstandDokumenteVollstaendig(dokumente: Set<String>): Boolean =
    listOf("lieferschein", "palettenschein", "qualitätskontrolle", "prozessdokumentation").all { it in dokumente }

private fun leitstandZustandCellColor(entry: All): CellColor =
    CellColor(Color(ZustandManager.colorHexFor(entry.zustand)))

private fun leitstandDokumentLkwNummer(lkw: All): String =
    lkw.lfdNr.takeIf { it > 0 }?.toString()
        ?: lkw.id.ifBlank { "1" }

private fun leitstandPalettenscheinFileName(lkw: All): String =
    "${lkw.lieferscheinnummer.ifBlank { "auftrag" }}-${leitstandDokumentLkwNummer(lkw)}_palettenschein.pdf"

private fun leitstandProzessdokumentationFileName(lkw: All): String =
    "${lkw.lieferscheinnummer.ifBlank { "auftrag" }}-${leitstandDokumentLkwNummer(lkw)}_prozessdokumentation.pdf"

private fun leitstandQualitaetsdokumentationFileName(lkw: All): String =
    "${lkw.lieferscheinnummer.ifBlank { "auftrag" }}_${leitstandDokumentLkwNummer(lkw)}_qualitaetsdokumentation.pdf"

private fun leitstandMatchesYardDocument(lkw: All, fileName: String, suffix: String): Boolean {
    val ls = lkw.lieferscheinnummer.ifBlank { "auftrag" }.lowercase()
    val lkwNr = leitstandDokumentLkwNummer(lkw).lowercase()
    val clean = fileName.lowercase()
    val suffixPattern = suffix.lowercase()
    val prefixDash = "$ls-$lkwNr"
    val prefixUnderscore = "${ls}_$lkwNr"
    val baseName = clean.removeSuffix(".pdf").removeSuffix(".json")
    return baseName.endsWith("_$suffixPattern") &&
        (baseName == "$prefixDash" + "_$suffixPattern" ||
            baseName == "$prefixUnderscore" + "_$suffixPattern" ||
            baseName.startsWith("$prefixDash-standort-") ||
            baseName.startsWith("${prefixUnderscore}_standort_"))
}

private fun leitstandMatchesWeFehlerprotokollDocument(lkw: All, fileName: String): Boolean =
    leitstandMatchesYardDocument(lkw, fileName, "we_fehlerprotokoll") ||
        leitstandMatchesYardDocument(lkw, fileName, "fehlerprotokoll") ||
        leitstandMatchesYardDocument(lkw, fileName, "wareneingangsbericht")

private suspend fun loadLeitstandAktivitaetsFeed(
    weAuftraege: List<All>,
    waLkw: List<All>,
    standortId: String,
    standortLabel: String
): List<All> {
    val logs = mutableListOf<All>()

    weAuftraege
        .filter { it.lfdNr > 0 }
        .distinctBy { "${it.firmenId}:${it.lfdNr}" }
        .forEach { auftrag ->
            logs += runCatching {
                YardApi.loadWeVorgangslog(auftrag.lfdNr, auftrag.firmenId)
            }.getOrDefault(emptyList())
        }

    waLkw
        .filter { it.lieferscheinnummer.isNotBlank() }
        .distinctBy { "${it.firmenId}:${it.lieferscheinnummer}" }
        .forEach { lkw ->
            logs += runCatching {
                YardApi.loadWaVorgangslog(lkw.lieferscheinnummer, lkw.firmenId)
            }.getOrDefault(emptyList())
        }

    return logs
        .filter { entry -> leitstandMatchesStandort(entry, standortId, standortLabel) }
        .filter { entry ->
            leitstandAktivitaetsTimestamp(entry)?.let { it >= Date.now().toLong() - 24L * 60L * 60L * 1000L } == true
        }
        .distinctBy { entry ->
            entry.id.ifBlank {
                "${entry.timestamp}:${entry.typ}:${entry.notiz}:${entry.lfdNr}:${entry.lieferscheinnummer}"
            }
        }
        .sortedBy { leitstandAktivitaetsTimestamp(it) ?: Long.MAX_VALUE }
        .takeLast(80)
}

private fun leitstandMatchesStandort(entry: All, standortId: String, standortLabel: String): Boolean {
    val id = standortId.trim()
    val label = standortLabel.trim()
    if (id.isBlank() && label.isBlank()) return true

    val primaryStandort = entry.standort.trim()
    if (primaryStandort.isNotBlank()) {
        if (id.isNotBlank() && primaryStandort == id) return true
        if (label.isNotBlank() && primaryStandort.equals(label, ignoreCase = true)) return true
        return false
    }

    val candidates = listOf(
        entry.slotStandortId,
        entry.standortId,
        entry.standort
    ).map { it.trim() }.filter { it.isNotBlank() }

    if (primaryStandort.isBlank() && candidates.isEmpty()) return true

    if (id.isNotBlank() && candidates.any { it == id }) return true
    if (label.isNotBlank() && candidates.any { it.equals(label, ignoreCase = true) }) return true
    return false
}

private fun leitstandAuskunftKopfHeaders(palettenDefs: List<All>): List<String> =
    listOf(
        "Typ", "Zustand", "Kontrolliert", "LS", "WA-Platz", "LKW Anzahl", "Ladestellen", "Prio",
        "Paletten Soll", "Paletten Ist", "Offen", "Paletten Eingang", "Paletten Ausgang", "LHM-Typ"
    ) + palettenDefs.map { it.bezeichnung }

private fun leitstandAuskunftKopfRows(
    weKopf: List<All>,
    waKopf: List<All>,
    waStandorteByTour: Map<String, List<All>>
): List<Pair<String, All>> =
    (
        weKopf.map { leitstandAuskunftKopfKey(it) to it } +
            waKopf.flatMap { kopf ->
                waStandorteByTour[kopf.lieferscheinnummer].orEmpty()
                    .ifEmpty { listOf(kopf) }
                    .map { standort ->
                        val row = if (standort === kopf) kopf else leitstandWaAuskunftStandortRow(kopf, standort)
                        leitstandAuskunftKopfKey(row) to row
                    }
            }
        )
        .sortedWith(
            compareBy<Pair<String, All>> { it.second.datum }
                .thenBy { if (leitstandAuftragsTyp(it.second) == "WE") 0 else 1 }
                .thenBy { leitstandLieferscheinnummer(it.second) }
                .thenBy { leitstandAuskunftWaStandortId(it.second).toIntOrNull() ?: Int.MAX_VALUE }
        )

private fun leitstandWaAuskunftStandortRow(kopf: All, standort: All): All =
    standort.copy(
        datum = standort.datum.ifBlank { kopf.datum },
        lieferant = standort.lieferant.ifBlank { kopf.lieferant },
        unternehmer = standort.unternehmer.ifBlank { kopf.unternehmer },
        auftraggeber = standort.auftraggeber.ifBlank { kopf.auftraggeber },
        empfaenger = standort.empfaenger.ifBlank { kopf.empfaenger },
        firma = standort.firma.ifBlank { kopf.firma },
        firmenId = standort.firmenId.ifBlank { kopf.firmenId },
        prio = standort.prio.ifBlank { kopf.prio },
        palettenanzahl = standort.palettenanzahl.takeIf { it > 0 }
            ?: standort.palettenWerte.values.sumOf { it.toIntOrNull() ?: 0 },
        auftragsart = "WA",
        waStandortId = standort.id,
        leitstandAuskunftWaStandortId = standort.id,
        standortId = standort.id,
        slotStandortId = standort.slotStandortId.ifBlank { kopf.slotStandortId }
    )

private fun leitstandAuskunftKopfRow(entry: All, weLkw: List<All>, waLkw: List<All>, palettenDefs: List<All>): List<String> {
    val typ = leitstandAuftragsTyp(entry)
    val matchingWaLkw = if (typ == "WA") {
        waLkw.filter { lkw -> leitstandWaLkwMatchesAuskunftStandort(lkw, entry) }
    } else {
        emptyList()
    }
    val matchingWeLkw = if (typ == "WE") {
        weLkw.filter { lkw -> leitstandWeSameAuftrag(entry, lkw) }
    } else {
        emptyList()
    }
    val palValues = palettenDefs.map { def ->
        if (typ == "WA") {
            entry.palettenWerte[def.bezeichnung]?.toIntOrNull()?.takeIf { it > 0 }?.toString().orEmpty()
        } else {
            entry.palettenWerte[def.bezeichnung].orEmpty()
        }
    }
    val soll = if (typ == "WA") {
        palValues.sumOf { it.toIntOrNull() ?: 0 }
    } else {
        entry.palettenanzahl
    }
    val ist = if (typ == "WA") matchingWaLkw.sumOf { leitstandWaIstPalettenanzahl(it) } else matchingWeLkw.sumOf { leitstandWeIstPalettenanzahl(it) }
    val lkwCount = if (typ == "WA") matchingWaLkw.size else matchingWeLkw.size.coerceAtLeast(1)
    val leerpaletten = if (typ == "WA") matchingWaLkw.sumOf { it.leerpalettenanzahl } else matchingWeLkw.sumOf { if (leitstandWeAbschlussBestaetigt(it)) it.leerpalettenanzahl else 0 }
    val offen = (soll - ist).coerceAtLeast(0)

    return listOf(
        typ,
        "${entry.zustand} - ${leitstandZustandText(typ, entry.zustand)}",
        if (typ == "WA") leitstandKontrolliertCheckbox(entry.kontrolliert) else "",
        leitstandLieferscheinnummer(entry),
        entry.platzcode,
        lkwCount.toString(),
        leitstandAuskunftLadestellen(entry),
        entry.ablaufPrio.takeIf { it > 0 }?.toString().orEmpty().ifBlank { entry.prio },
        soll.takeIf { it > 0 }?.toString().orEmpty(),
        ist.takeIf { it > 0 }?.toString().orEmpty(),
        offen.takeIf { soll > 0 }?.toString().orEmpty(),
        if (typ == "WA") leerpaletten.takeIf { it > 0 }?.toString().orEmpty() else ist.takeIf { it > 0 }?.toString().orEmpty(),
        if (typ == "WE" && matchingWeLkw.any { leitstandWeAbschlussBestaetigt(it) }) leerpaletten.toString() else if (typ == "WA") ist.takeIf { it > 0 }?.toString().orEmpty() else "",
        entry.slotLhmTyp
    ) + palValues
}

private fun leitstandWeSameAuftrag(kopf: All, lkw: All): Boolean =
    (leitstandWeSammelKey(kopf).isNotBlank() && leitstandWeSammelKey(kopf) == leitstandWeSammelKey(lkw)) ||
    (kopf.lieferscheinnummer.isNotBlank() && kopf.lieferscheinnummer == lkw.lieferscheinnummer) ||
        (kopf.id.isNotBlank() && kopf.id == lkw.id) ||
        (kopf.lfdNr > 0 && lkw.lfdNr > 0 && kopf.lfdNr == lkw.lfdNr)

private fun leitstandAuskunftDetails(
    selectedKey: String?,
    weLkw: List<All>,
    waLkw: List<All>,
    waStandorteByTour: Map<String, List<All>>
): List<All> {
    val key = selectedKey.orEmpty()
    val parts = key.split(":")
    val typ = parts.getOrNull(0).orEmpty()
    val lfdNr = parts.getOrNull(1).orEmpty()
    val ref = parts.getOrNull(2).orEmpty()
    val standortId = parts.drop(3).joinToString(":")
    return when {
        typ == "WE" -> weLkw.filter { entry ->
            if (ref.isNotBlank()) entry.lieferscheinnummer == ref else entry.lfdNr.toString() == lfdNr
        }
        typ == "WA" -> waLkw.filter { entry ->
            val sameTour = if (ref.isNotBlank()) entry.lieferscheinnummer == ref else entry.lfdNr.toString() == lfdNr
            if (!sameTour) return@filter false
            if (standortId.isBlank()) return@filter true
            val standort = waStandorteByTour[entry.lieferscheinnummer].orEmpty()
                .firstOrNull { it.id == standortId }
            standort != null && leitstandWaLkwMatchesStandort(entry, standort)
        }
        else -> emptyList()
    }
}

private fun leitstandAuskunftDetailHeaders(
    weKontrollen: List<All>,
    waKontrollen: List<All>
): List<String> =
    listOf(
        "Datum", "Typ", "Zeitfenster", "Angemeldet", "Lieferschein", "Palettenschein", "Qualitätskontrolle", "Prozessdokumentation", "Zustand", "LS", "PIN", "Tor",
        "Fahrer", "LKW", "Auflieger", "WA Platz", "Paletten Soll", "Paletten Ist", "Offen",
        "Paletten Eingang", "Paletten Ausgang", "Vorgeladen", "WE Fehlerprotokoll", "Bruch", "Fehlmenge", "Überzählig",
        "Lieferant", "Unternehmer", "Auftraggeber", "Empfänger"
    )

private fun leitstandAuskunftDetailRow(
    entry: All,
    waStandorteByTour: Map<String, List<All>>,
    weKontrollen: List<All>,
    waKontrollen: List<All>,
    angekommenByAuftrag: Map<String, String>,
    dokumentStatus: Map<String, Set<String>>
): List<String> {
    val typ = leitstandAuftragsTyp(entry)
    val waStandort = leitstandWaStandortForLkw(entry, waStandorteByTour)
    val soll = entry.palettenanzahl
    val ist = if (typ == "WA") leitstandWaIstPalettenanzahl(entry) else leitstandWeIstPalettenanzahl(entry)
    val bruch = if (typ == "WE") leitstandWeExtraInt(entry, entry.mobilWePalettenBruch) else 0
    val fehlmenge = if (typ == "WE") leitstandWeExtraInt(entry, entry.mobilWePalettenFehlmenge) else 0
    val ueberzaehlig = if (typ == "WE") leitstandWeExtraInt(entry, entry.mobilWePalettenUeberzaehlig) else 0
    val eingang = if (typ == "WA") entry.leerpalettenanzahl else ist
    val ausgang = if (typ == "WA") ist else if (leitstandWeAbschlussBestaetigt(entry)) entry.leerpalettenanzahl else 0
    val offen = (soll - ist).coerceAtLeast(0)
    val dokumente = dokumentStatus[leitstandLkwRowKey(entry)].orEmpty()
    return listOf(
        entry.datum,
        typ,
        leitstandTerminZeitfenster(entry),
        leitstandAngekommenAnzeige(entry, angekommenByAuftrag),
        leitstandDokumentCheckbox(dokumente, "lieferschein"),
        leitstandDokumentCheckbox(dokumente, "palettenschein"),
        leitstandDokumentCheckbox(dokumente, "qualitätskontrolle"),
        leitstandDokumentCheckbox(dokumente, "prozessdokumentation"),
        "${entry.zustand} - ${leitstandZustandText(typ, entry.zustand)}",
        leitstandLieferscheinnummer(entry),
        yardPinCode(entry),
        leitstandTorAnzeige(entry, waStandort),
        entry.fahrername,
        entry.lkwkennzeichen,
        entry.aufliegerkennzeichen,
        if (typ == "WA") leitstandPlatzAnzeige(entry, waStandort) else "",
        soll.takeIf { it > 0 }?.toString().orEmpty(),
        ist.takeIf { it > 0 }?.toString().orEmpty(),
        offen.toString(),
        eingang.takeIf { it > 0 }?.toString().orEmpty(),
        if (typ == "WE" && leitstandWeAbschlussBestaetigt(entry)) ausgang.toString() else ausgang.takeIf { it > 0 }?.toString().orEmpty(),
        leitstandVorgeladenPalettenAnzeige(entry, typ),
        leitstandDokumentCheckbox(dokumente, "we fehlerprotokoll"),
        bruch.takeIf { it > 0 }?.toString().orEmpty(),
        fehlmenge.takeIf { it > 0 }?.toString().orEmpty(),
        ueberzaehlig.takeIf { it > 0 }?.toString().orEmpty(),
        entry.lieferant,
        entry.unternehmer,
        entry.auftraggeber,
        entry.empfaenger
    )
}

private fun leitstandKontrollLabels(weKontrollen: List<All>, waKontrollen: List<All>): List<String> =
    leitstandKontrollDefinitions(weKontrollen, waKontrollen).map { def ->
        def.kontrollBezeichnung.ifBlank { def.bezeichnung }
    }

private fun leitstandKontrollDefinitions(weKontrollen: List<All>, waKontrollen: List<All>): List<All> =
    (weKontrollen + waKontrollen)
        .filter { it.kontrollBezeichnung.isNotBlank() || it.bezeichnung.isNotBlank() }
        .distinctBy { it.kontrollBezeichnung.ifBlank { it.bezeichnung } }

private fun leitstandKontrollValue(entry: All, def: All): String =
    leitstandNormalizeKontrollAnzeige(
        entry.palettenWerte["mobil.kontrolle.${def.id.ifBlank { def.kontrollId }}"].orEmpty()
        .ifBlank { entry.palettenWerte[def.kontrollBezeichnung].orEmpty() }
        .ifBlank { entry.palettenWerte[def.bezeichnung].orEmpty() },
        def
    )

private fun leitstandNormalizeKontrollAnzeige(value: String, def: All): String {
    if (!def.kontrollTyp.equals("checkbox", true)) return value
    return when (value.trim().lowercase()) {
        "\u2705", "ja", "true", "1" -> "\u2705"
        "\u2B1C", "nein", "false", "0" -> "\u2B1C"
        else -> value
    }
}

private fun leitstandAuskunftKopfKey(entry: All): String {
    val typ = leitstandAuftragsTyp(entry)
    val ref = entry.lieferscheinnummer
    val standortId = if (typ == "WA") leitstandAuskunftWaStandortId(entry) else ""
    return listOf(typ, entry.lfdNr.toString(), ref, standortId).joinToString(":")
}

private fun leitstandAuskunftWaStandortId(entry: All): String =
    entry.leitstandAuskunftWaStandortId

private fun leitstandAuskunftStandortAnzeige(entry: All): String =
    entry.platzcode.ifBlank { entry.platz }
        .ifBlank { entry.standort }
        .ifBlank { entry.ort }

private fun leitstandAuskunftLadestellen(entry: All): String =
    entry.standort.ifBlank { entry.ort }

private fun leitstandWaLkwMatchesAuskunftStandort(lkw: All, auskunftRow: All): Boolean {
    if (lkw.lieferscheinnummer != auskunftRow.lieferscheinnummer) return false
    val standortId = leitstandAuskunftWaStandortId(auskunftRow)
    if (standortId.isBlank()) return true
    return leitstandWaLkwMatchesStandort(lkw, auskunftRow)
}

private fun leitstandAuskunftDetailKey(entry: All): String =
    "${leitstandAuftragsTyp(entry)}:${entry.id}:${entry.lfdNr}:${entry.lkwkennzeichen}:${leitstandLieferscheinnummer(entry)}"

private fun leitstandLkwRowKey(entry: All): String =
    when (leitstandAuftragsTyp(entry)) {
        "WE" -> "WE:${entry.lfdNr}:${entry.lieferscheinnummer}"
        "WA" -> "WA:${entry.lieferscheinnummer}:${entry.id}"
        else -> "FEHLER:${entry.id}:${entry.lfdNr}:${entry.lieferscheinnummer}"
    }

private fun leitstandAuftragsTyp(entry: All): String =
    entry.auftragsart.trim().uppercase()
        .ifBlank { entry.slotBereich.trim().uppercase() }
        .takeIf { it == "WE" || it == "WA" }
        ?: "FEHLER"

private fun leitstandZustandText(typ: String, zustand: Int): String =
    if (typ == "WE") {
        when (zustand) {
            ZustandManager.IN_VERLADUNG -> "In Entladung"
            ZustandManager.TEILENTLADEN -> "Teilentladen"
            else -> ZustandManager.textFor(zustand)
        }
    } else {
        ZustandManager.textFor(zustand)
    }

private fun leitstandAngekommenZeiten(logs: List<All>): Map<String, String> =
    logs
        .filter { it.typ == "LKW_EINGETROFFEN" }
        .mapNotNull { entry ->
            val time = leitstandAktivitaetsDatumUhrzeit(entry).ifBlank { return@mapNotNull null }
            leitstandAuftragKeys(entry).map { it to time }
        }
        .flatten()
        .toMap()

private fun leitstandAngekommenAnzeige(entry: All, angekommenByAuftrag: Map<String, String>): String =
    leitstandAuftragKeys(entry)
        .firstNotNullOfOrNull { key -> angekommenByAuftrag[key]?.takeIf { it.isNotBlank() } }
        .orEmpty()

private fun leitstandAuftragKeys(entry: All): List<String> {
    val typ = leitstandAuftragsTyp(entry)
    val ls = entry.lieferscheinnummer.trim()
    val lfdNr = entry.lfdNr.takeIf { it > 0 }?.toString().orEmpty()
    return buildList {
        if (typ.isNotBlank() && lfdNr.isNotBlank()) add("$typ:lfd:$lfdNr")
        if (typ.isNotBlank() && ls.isNotBlank()) add("$typ:ls:$ls")
    }
}

private fun leitstandAktivitaetsDatumUhrzeit(entry: All): String {
    val timestamp = leitstandAktivitaetsTimestamp(entry)
    if (timestamp != null) {
        val date = Date(timestamp.toDouble())
        val tag = date.getDate().toString().padStart(2, '0')
        val monat = (date.getMonth() + 1).toString().padStart(2, '0')
        val jahr = date.getFullYear().toString()
        val uhrzeit = "${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}"
        return "$tag.$monat.$jahr $uhrzeit"
    }
    return ""
}

private fun leitstandDatumUhrzeit(datum: String, uhrzeit: String): String {
    val date = leitstandDatumDeutsch(datum)
    val time = uhrzeit.trim()
    return listOf(date, time).filter { it.isNotBlank() }.joinToString(" ")
}

private fun leitstandLieferscheinnummer(lkw: All): String =
    lkw.lieferscheinnummer

private fun leitstandWaIstPalettenanzahl(entry: All): Int =
    entry.yardabschlussIstPaletten.takeIf { it > 0 }
        ?: entry.mobilIstPaletten.takeIf { it > 0 }
        ?: entry.palettenanzahl.takeIf { entry.zustand >= ZustandManager.ABGESCHLOSSEN && it > 0 }
        ?: 0

private fun leitstandVorgeladenPaletten(entry: All): Int =
    entry.driverVorgeladenPaletten

private fun leitstandVorgeladenPalettenAnzeige(entry: All, typ: String): String =
    if (typ == "WA") leitstandVorgeladenPaletten(entry).toString() else ""

private fun leitstandBearbeiterKuerzel(entry: All): String =
    entry.mobileBearbeiterKuerzel
        .ifBlank {
            entry.mobileBearbeiterName
                .split(" ")
                .mapNotNull { it.trim().firstOrNull()?.uppercaseChar()?.toString() }
                .joinToString("")
        }

private fun leitstandMitarbeiterName(user: All): String =
    listOf(user.vorname, user.nachname).filter { it.isNotBlank() }.joinToString(" ")
        .ifBlank { user.personalnummer.ifBlank { user.id } }

private fun leitstandMitarbeiterKuerzel(user: All): String =
    user.kuerzel.ifBlank {
        listOf(user.vorname, user.nachname)
            .mapNotNull { it.trim().firstOrNull()?.uppercaseChar()?.toString() }
            .joinToString("")
            .ifBlank { user.personalnummer.ifBlank { user.id } }
    }

private fun leitstandMitarbeiterAuswahlLabel(user: All): String {
    val name = leitstandMitarbeiterName(user)
    val kuerzel = leitstandMitarbeiterKuerzel(user)
    return if (name.equals(kuerzel, ignoreCase = true)) name else "$kuerzel – $name"
}

private fun leitstandWeAbschlussBestaetigt(entry: All): Boolean =
    entry.mobilPalettenAbgeladenBestaetigt

private fun leitstandWeIstPalettenanzahl(entry: All): Int =
    if (leitstandWeAbschlussBestaetigt(entry)) {
        entry.yardabschlussIstPaletten.takeIf { it > 0 }
            ?: entry.mobilWePalettenUnbeschaedigt.takeIf { it > 0 }
            ?: entry.palettenanzahl
    } else {
        0
    }

private fun leitstandWeExtraInt(entry: All, value: Int): Int =
    if (leitstandWeAbschlussBestaetigt(entry)) value else 0

private fun leitstandWaLkwMatchesStandort(lkw: All, standort: All): Boolean =
    (standort.id.isNotBlank() && lkw.waStandortId == standort.id) ||
        (standort.id.isNotBlank() && lkw.standort == standort.id) ||
        (standort.standort.isNotBlank() && lkw.standort == standort.standort) ||
        (standort.ort.isNotBlank() && lkw.ort == standort.ort) ||
        (standort.platzcode.isNotBlank() && lkw.platzcode == standort.platzcode)

private fun leitstandWaStandortForLkw(lkw: All, waStandorteByTour: Map<String, List<All>>): All? {
    if (lkw.lieferscheinnummer.isBlank()) return null
    val standorte = waStandorteByTour[lkw.lieferscheinnummer].orEmpty()
    if (standorte.isEmpty()) return null
    return standorte.firstOrNull { standort -> leitstandWaLkwMatchesStandort(lkw, standort) }
        ?: standorte.firstOrNull()
}

private fun leitstandPlatzAnzeige(lkw: All, waStandort: All?): String =
    waStandort?.platzcode.orEmpty()
        .ifBlank { lkw.platzcode }
        .ifBlank { lkw.platz }

private fun leitstandTorAnzeige(
    lkw: All,
    waStandort: All? = null,
    torBySammelKey: Map<String, String> = emptyMap()
): String =
    torBySammelKey[leitstandSammelKey(lkw)].orEmpty()
        .ifBlank { waStandort?.tor?.toString().orEmpty() }
        .ifBlank { lkw.tor?.toString().orEmpty() }

private fun leitstandSammelKey(entry: All): String =
    when (leitstandAuftragsTyp(entry)) {
        SLOT_BEREICH_WE -> leitstandWeSammelKey(entry)
        SLOT_BEREICH_WA -> leitstandWaSammelKey(entry)
        else -> ""
    }

private fun leitstandWeSammelKey(entry: All): String =
    entry.weSammelId.trim()

private fun leitstandWaSammelKey(entry: All): String =
    entry.waSammelPin
        .ifBlank { entry.waSammelId }
        .ifBlank { entry.yardPin }
        .ifBlank { entry.pin }
        .trim()

private fun leitstandTerminZeitfenster(lkw: All): String {
    val datum = lkw.slotDatum
        .ifBlank { lkw.verladedatum }
        .ifBlank { lkw.datum }
    val von = lkw.uhrzeitVon
        .ifBlank { lkw.uhrzeitVon }
        .ifBlank { lkw.verladeuhrzeit }
    val bis = lkw.uhrzeitBis
        .ifBlank { lkw.uhrzeitBis }

    val zeit = when {
        von.isNotBlank() && bis.isNotBlank() -> "$von - $bis"
        von.isNotBlank() -> "Start $von"
        bis.isNotBlank() -> "bis $bis"
        else -> ""
    }

    return listOf(leitstandDatumDeutsch(datum), zeit)
        .filter { it.isNotBlank() }
        .joinToString(" ")
}

private fun leitstandDatumDeutsch(value: String): String {
    if (!value.contains("-") || value.length != 10) return value
    val parts = value.split("-")
    return if (parts.size == 3) "${parts[2]}.${parts[1]}.${parts[0]}" else value
}

private fun leitstandAktivitaetsTimestamp(entry: All): Long? =
    entry.timestamp.toLongOrNull()
